import { Dispatch } from "react";
import { AxiosResponse } from "axios";

import {
  Module,
  EndPoint,
  getApiCall,
  postApiCall,
} from "../../../common/constants";
import {
  AnalyticsEvents,
  capturePosthogEvent,
} from "../../../common/utils/analytics";
import { RootState } from "../../../redux/store";
import { setLoading } from "../../../redux/global.slice";
import { SetSubmittingType } from "../../../redux/global.types";
import { setAccountDetails, setAccountListingData } from "./slice";
import { apiSuccessCode } from "../../../common/constants/axios.instance";
import { checkInternetAndShowAlert, showAlert } from "../../../common/utils";

export const onAddAccount =
  (
    encryptedData: string,
    setSubmitting: SetSubmittingType,
    callback?: () => void,
  ) =>
  (dispatch: Dispatch<any>, getState: () => RootState) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    setSubmitting(true);
    dispatch(setLoading(true));

    const dataToSend = {
      encryptedData,
    };

    postApiCall(
      Module.manufacturer + EndPoint.addAccount,
      dataToSend,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;

        if (data?.statusCode === apiSuccessCode.postApiSuccess) {
          const uid = getState().verifyEmail.userId;
          capturePosthogEvent(AnalyticsEvents.BANK_DETAILS_UPDATED, {
            user_id: uid,
            aeropay_identifier:
              data?.data?.bankAccountId ??
              data?.data?.aeropayConsumerId ??
              data?.data?.identifier ??
              "",
          });
          showAlert(1, data.message);
          if (callback) callback();
        }

        setSubmitting(false);
        dispatch(setLoading(false));
      },
      (error: any) => {
        setSubmitting(false);
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const getAccountListing = () => (dispatch: Dispatch<any>) => {
  // Check for network connectivity
  if (!checkInternetAndShowAlert(showAlert)) {
    return;
  }
  dispatch(setLoading(true));

  getApiCall(
    Module.manufacturer,
    `${EndPoint.linkedAccountListing}`,
    (response: AxiosResponse<any>) => {
      const { statusCode, data } = response.data;

      if (statusCode === apiSuccessCode.success) {
        dispatch(setAccountListingData(data));
      }
      dispatch(setLoading(false));
    },
    (error: any) => {
      dispatch(setLoading(false));
      showAlert(2, error.message);
    },
  );
};

export const onSetPrimaryAccount = (
  bankAccountId: string,
  callback?: () => void,
  isCalledThroughPayment?: boolean,
) => {
  return (dispatch: any, getState: () => RootState) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }
    dispatch(setLoading(true));

    postApiCall(
      `${Module.manufacturer}${EndPoint.setPrimaryAccount}/${bankAccountId}`,
      {},

      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.ok) {
          if (!isCalledThroughPayment) showAlert(1, data.message);
          const uid = getState().verifyEmail.userId;
          dispatch(getAccountListing());
          capturePosthogEvent(AnalyticsEvents.BANK_DETAILS_UPDATED, {
            action: "set_primary_account",
            user_id: uid,
            bankAccountId,
          });
          if (callback) callback();
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        showAlert(2, error.message);
        dispatch(setLoading(false));
      },
    );
  };
};

export const onGetPlaidLink = (payload: any) => {
  return (dispatch: any) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }
    dispatch(setLoading(true));

    postApiCall(
      `${Module.manufacturer}${EndPoint.getPlaidLink}`,
      payload,

      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.success) {
          const plaidLink = data.data.plaidLink;
          if (plaidLink) {
            window.location.href = plaidLink;
          }
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        showAlert(2, error.message);
        dispatch(setLoading(false));
      },
    );
  };
};

export const getAccountDetails =
  (sourceId: string) => (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }
    dispatch(setLoading(true));

    getApiCall(
      Module.manufacturer,
      `${EndPoint.accountListing}/${sourceId}`,
      (response: AxiosResponse<any>) => {
        const { statusCode, data } = response.data;

        if (statusCode === apiSuccessCode.success) {
          dispatch(setAccountDetails(data));
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const verifyAccount = (values: any, callback?: () => void) => {
  return (dispatch: any) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }
    dispatch(setLoading(true));

    postApiCall(
      `${Module.manufacturer}${EndPoint.verifyAccount}`,
      values,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.success) {
          if (callback) callback();
          const verificationLink = data.data.verificationLink;
          if (verificationLink) {
            window.location.href = verificationLink; // Open in a new tab
          }
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        showAlert(2, error.message);
        dispatch(setLoading(false));
      },
    );
  };
};
